import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Radar, Kanban, Package, Truck, DollarSign, Calculator,
  FileText, Users, Store, BarChart3, Settings, Search, Bell, LogOut, Menu, X, ShieldCheck, History,
} from "lucide-react";
import { api, backendUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/radar", label: "Radar PNCP", icon: Radar, id: "radar" },
  { to: "/pipeline", label: "Pipeline", icon: Kanban, id: "pipeline" },
  { to: "/benchmark", label: "Benchmark", icon: History, id: "benchmark" },
  { to: "/produtos", label: "Produtos", icon: Package, id: "produtos" },
  { to: "/fornecedores", label: "Fornecedores", icon: Truck, id: "fornecedores" },
  { to: "/financeiro", label: "Financeiro & DRE", icon: DollarSign, id: "financeiro" },
  { to: "/calculadora", label: "Calculadora de Preço", icon: Calculator, id: "calculadora" },
  { to: "/nfe", label: "Notas Fiscais", icon: FileText, id: "nfe" },
  { to: "/crm", label: "CRM", icon: Users, id: "crm", soon: true },
  { to: "/marketplaces", label: "Marketplaces", icon: Store, id: "marketplaces", soon: true },
  { to: "/relatorios", label: "Relatórios & BI", icon: BarChart3, id: "relatorios", soon: true },
  { to: "/configuracoes", label: "Configurações", icon: Settings, id: "configuracoes" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [logoErr, setLogoErr] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
  };

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/radar?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const Brand = () => (
    <div className="flex items-center gap-2.5" data-testid="company-logo-slot">
      {company?.logo_path && !logoErr ? (
        <img
          src={`${backendUrl}/api/company/logo/raw?t=${company.logo_path}`}
          alt="Logo"
          onError={() => setLogoErr(true)}
          className="h-9 w-9 rounded-lg object-contain bg-white border border-slate-200"
        />
      ) : (
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-slate-900 text-white">
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
        </div>
      )}
      <div className="leading-tight">
        <div className="font-display text-sm font-extrabold tracking-tight text-slate-900">PRO INVICTA</div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-600">HUB</div>
      </div>
    </div>
  );

  const SidebarInner = () => (
    <>
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-4">
        <Brand />
        <button className="lg:hidden text-slate-500" onClick={() => setOpen(false)} data-testid="sidebar-close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {NAV.map((n) => (
          <NavLink
            key={n.id}
            to={n.to}
            data-testid={`sidebar-link-${n.id}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-sky-50 text-sky-700"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            <span className="flex-1">{n.label}</span>
            {n.soon && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-400">
                em breve
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-sky-100 text-sm font-bold text-sky-700">
            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-slate-800">{user?.name || "Usuário"}</div>
            <div className="truncate text-xs text-slate-400 capitalize">{user?.role}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-rose-600" title="Sair">
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex lg:fixed lg:inset-y-0">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-6 w-6" />
          </button>
          <form onSubmit={submitSearch} className="relative flex-1 max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar oportunidades, órgãos, objeto..."
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-sky-400 focus:bg-white"
            />
          </form>
          <div className="hidden sm:block text-sm font-medium text-slate-500">
            {greet()}, <span className="font-semibold text-slate-800">PRO INVICTA</span>.
          </div>
          <button className="relative text-slate-500 hover:text-slate-800" data-testid="notifications-btn">
            <Bell className="h-5 w-5" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-500" />
          </button>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
